import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type CartItem = {
  slug: string;
  name: string;
  price: number;
  img: string;
  color: string;
  size?: string;
  quantity: number;
};

type CartCtx = {
  items: CartItem[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  add: (item: CartItem) => void;
  updateQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  keyOf: (item: Pick<CartItem, "slug" | "color" | "size">) => string;
};

const CartContext = createContext<CartCtx | null>(null);

const STORAGE_KEY = "nv_cart_v1";

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const keyOf = useCallback(
    (i: Pick<CartItem, "slug" | "color" | "size">) => `${i.slug}__${i.color}__${i.size ?? ""}`,
    []
  );

  const add = useCallback(
    (item: CartItem) => {
      setItems((prev) => {
        const k = keyOf(item);
        const idx = prev.findIndex((p) => keyOf(p) === k);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + item.quantity };
          return next;
        }
        return [...prev, item];
      });
      setIsOpen(true);
    },
    [keyOf]
  );

  const updateQty = useCallback(
    (key: string, qty: number) => {
      setItems((prev) =>
        prev
          .map((p) => (keyOf(p) === key ? { ...p, quantity: Math.max(1, qty) } : p))
      );
    },
    [keyOf]
  );

  const remove = useCallback(
    (key: string) => setItems((prev) => prev.filter((p) => keyOf(p) !== key)),
    [keyOf]
  );

  const value = useMemo<CartCtx>(
    () => ({
      items,
      count: items.reduce((n, i) => n + i.quantity, 0),
      subtotal: items.reduce((n, i) => n + i.price * i.quantity, 0),
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      add,
      updateQty,
      remove,
      keyOf,
    }),
    [items, isOpen, add, updateQty, remove, keyOf]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}